import React, { useEffect, useState } from 'react';
import { Table, Input, Button, Modal, Form, InputNumber, message } from 'antd';
import { observer } from 'mobx-react-lite';
import { EnterpriseAccountManagementPageModel } from '../pageModels/EnterpriseAccountManagementPageModel';
import { EnterpriseAccount, ChangeRecord } from '../../models/EnterpriseAccount';
import { formatDate } from '../../utils/DateUtil';
import '../styles/EnterpriseAccountManagement.css';
import { globalStore } from '../../models/globalStore';

const pageModel = new EnterpriseAccountManagementPageModel();

const EnterpriseAccountManagement: React.FC = observer(() => {
    const [withdrawVisible, setWithdrawVisible] = useState(false);
    const [chargeVisible, setChargeVisible] = useState(false);
    const [cashbackVisible, setCashbackVisible] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        globalStore.selectedMenuKey = 'enterprise-account-management';
        pageModel.loadAccounts();
    }, []);

    const columns = [
        {
            title: '卖家信息',
            dataIndex: 'sellerInfo',
            render: (_: any, record: EnterpriseAccount) => (
                <div style={{ cursor: 'default' }}>
                    <div>ID: {record.sellerId}</div>
                    <div>手机: {record.sellerPhone}</div>
                    <div>昵称: {record.sellerNickname}</div>
                </div>
            ),
            width: 200
        },
        {
            title: '企业信息',
            dataIndex: 'enterpriseInfo',
            render: (_: any, record: EnterpriseAccount) => (
                <div style={{ cursor: 'default' }}>
                    <div>{record.enterpriseName}</div>
                    <div>{record.taxNo}</div>
                </div>
            ),
            width: 200
        },
        {
            title: '可用余额',
            dataIndex: 'totalRestMoney',
            render: (value: number, record: EnterpriseAccount) => (
                <span 
                    className="clickable-money"
                    onClick={() => pageModel.loadChangeRecords(record)}
                >
                    {value.toFixed(2)}
                </span>
            ),
            width: 120,
            align: 'right' as const
        },
        {
            title: '提现冻结金额',
            dataIndex: 'withdrawFreezeAmount',
            render: (value: number, record: EnterpriseAccount) => (
                <span 
                    className="clickable-money"
                    onClick={() => pageModel.loadChangeRecords(record)}
                >
                    {value.toFixed(2)}
                </span>
            ),
            width: 120,
            align: 'right' as const
        },
        {
            title: '总充值金额',
            dataIndex: 'totalChargedMoney',
            render: (value: number, record: EnterpriseAccount) => (
                <span 
                    className="clickable-money"
                    onClick={() => pageModel.loadChangeRecords(record)}
                >
                    {value.toFixed(2)}
                </span>
            ),
            width: 120,
            align: 'right' as const
        },
        {
            title: '总提现金额',
            dataIndex: 'totalWithdrawMoney',
            render: (value: number, record: EnterpriseAccount) => (
                <span 
                    className="clickable-money"
                    onClick={() => pageModel.loadChangeRecords(record)}
                >
                    {value.toFixed(2)}
                </span>
            ),
            width: 120,
            align: 'right' as const
        },
        {
            title: '操作',
            key: 'action',
            align: 'center' as const,
            render: (record: EnterpriseAccount) => (
                <div style={{ textAlign: 'center' }}>
                    <span 
                        className="operation-btn view-btn"
                        onClick={() => pageModel.loadChangeRecords(record)}
                    >
                        变动记录
                    </span>
                    {record.withdrawFreezeAmount === 0 ? (
                        <span 
                            className="operation-btn withdraw-btn"
                            onClick={() => handleOperationClick('registerWithdraw', record)}
                        >
                            提现登记
                        </span>
                    ) : (
                        <span 
                            className="operation-btn withdraw-btn"
                            onClick={() => handleOperationClick('finishWithdraw', record)}
                        >
                            完成提现
                        </span>
                    )}
                    <span 
                        className="operation-btn charge-btn"
                        onClick={() => handleOperationClick('charge', record)}
                    >
                        充值
                    </span>
                    <span 
                        className="operation-btn cashback-btn"
                        onClick={() => handleOperationClick('cashback', record)}
                    >
                        返现
                    </span>
                </div>
            ),
            width: 300
        }
    ];

    const changeRecordColumns = [
        {
            title: '时间',
            dataIndex: 'createTime',
            render: (value: Date) => formatDate(value),
            width: 180
        },
        {
            title: '类型',
            dataIndex: 'type',
            render: (type: number) => {
                const types = ['提现', '充值', '购买订单', '订单退款', '平台返现'];
                return types[type];
            },
            width: 100
        },
        {
            title: '金额',
            dataIndex: 'amount',
            render: (value: number) => value.toFixed(2),
            width: 120
        },
        {
            title: '描述',
            dataIndex: 'description'
        }
    ];

    const handleOperationClick = (type: 'registerWithdraw' | 'finishWithdraw' | 'charge' | 'cashback', account: EnterpriseAccount) => {
        pageModel.selectedAccount = account;
        form.resetFields();
        switch (type) {
            case 'registerWithdraw':
                setWithdrawVisible(true);
                break;
            case 'finishWithdraw':
                setWithdrawVisible(true);
                break;
            case 'charge':
                setChargeVisible(true);
                break;
            case 'cashback':
                setCashbackVisible(true);
                break;
        }
    };

    const handleWithdraw = async () => {
        try {
            const values = await form.validateFields();
            if (pageModel.selectedAccount?.withdrawFreezeAmount === 0) {
                // 提现登记
                await pageModel.registerWithdraw(values.money, values.bankNo);
            } else {
                // 完成提现
                await pageModel.finishWithdraw(values.money, values.bankTransferNo);
            }
            setWithdrawVisible(false);
        } catch (error) {
            // Form validation error
        }
    };

    const handleCharge = async () => {
        try {
            const values = await form.validateFields();
            await pageModel.charge(values.money, values.bankTransferNo, values.paymentAccount);
            setChargeVisible(false);
        } catch (error) {
            // Form validation error
        }
    };

    const handleCashback = async () => {
        try {
            const values = await form.validateFields();
            await pageModel.cashback(values.money, values.reason);
            setCashbackVisible(false);
        } catch (error) {
            // Form validation error
        }
    };

    const validatePositiveMoney = (_: any, value: number) => {
        if (!value || value <= 0) {
            return Promise.reject(new Error('金额必须大于0'));
        }
        return Promise.resolve();
    };

    return (
        <div className="enterprise-account-management">
            <div className="search-area">
                <Input
                    placeholder="卖家ID/手机号/企业名称/税号"
                    value={pageModel.searchText}
                    onChange={e => pageModel.setSearchText(e.target.value)}
                    style={{ width: 300 }}
                />
                <Button type="primary" onClick={() => pageModel.loadAccounts()}>查询</Button>
                <Button onClick={pageModel.resetSearch}>重置</Button>
            </div>

            <Table
                columns={columns}
                dataSource={pageModel.accounts}
                loading={pageModel.loading}
                rowKey="sellerId"
                pagination={false}
                scroll={{ x: 'max-content' }}
            />

            <Modal
                title="变动记录"
                open={pageModel.changeRecordsVisible}
                onCancel={() => pageModel.setChangeRecordsVisible(false)}
                footer={null}
                width={800}
            >
                <Table
                    columns={changeRecordColumns}
                    dataSource={pageModel.changeRecords}
                    rowKey="id"
                    pagination={{
                        current: pageModel.currentPage,
                        pageSize: pageModel.pageSize,
                        onChange: pageModel.setCurrentPage,
                        total: pageModel.changeRecords.length
                    }}
                />
            </Modal>

            <Modal
                title={pageModel.selectedAccount?.withdrawFreezeAmount === 0 ? "提现登记" : "完成提现"}
                open={withdrawVisible}
                onOk={handleWithdraw}
                onCancel={() => setWithdrawVisible(false)}
            >
                <Form form={form}>
                    <Form.Item
                        name="money"
                        label="提现金额"
                        rules={[
                            { required: true, message: '请输入提现金额' },
                            { validator: validatePositiveMoney }
                        ]}
                    >
                        <InputNumber style={{ width: '100%' }} precision={2} />
                    </Form.Item>
                    {pageModel.selectedAccount?.withdrawFreezeAmount === 0 ? (
                        <Form.Item
                            name="bankNo"
                            label="银行卡号"
                            rules={[{ required: true, message: '请输入对方银行卡号' }]}
                        >
                            <Input placeholder='请输入对方银行卡号' />
                        </Form.Item>
                    ) : (
                        <Form.Item
                            name="bankTransferNo"
                            label="付款编号"
                            rules={[{ required: true, message: '请输入付款编号' }]}
                        >
                            <Input placeholder='请输入付款编号' />
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            <Modal
                title="充值操作"
                open={chargeVisible}
                onOk={handleCharge}
                onCancel={() => setChargeVisible(false)}
            >
                <div style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
                    请再次输入企业信息进行确认，防止操作错误
                </div>
                <Form form={form}>
                    <Form.Item
                        name="enterpriseName"
                        label="企业全称"
                        rules={[
                            { required: true, message: '请输入企业全称' },
                            {
                                validator: (_: any, value: string) => {
                                    if (!value) {
                                        return Promise.resolve();
                                    }
                                    if (pageModel.selectedAccount && value !== pageModel.selectedAccount.enterpriseName) {
                                        return Promise.reject(new Error('企业全称与选中企业不匹配'));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Input placeholder='请输入企业全称' />
                    </Form.Item>
                    <Form.Item
                        name="taxNo"
                        label="税务识别号"
                        rules={[
                            { required: true, message: '请输入税务识别号' },
                            {
                                validator: (_: any, value: string) => {
                                    if (!value) {
                                        return Promise.resolve();
                                    }
                                    if (pageModel.selectedAccount && value !== pageModel.selectedAccount.taxNo) {
                                        return Promise.reject(new Error('税务识别号与选中企业不匹配'));
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Input placeholder='请输入税务识别号' />
                    </Form.Item>
                    <Form.Item
                        name="money"
                        label="充值金额"
                        rules={[
                            { required: true, message: '请输入充值金额' },
                            { validator: validatePositiveMoney }
                        ]}
                    >
                        <InputNumber style={{ width: '100%' }} precision={2} />
                    </Form.Item>
                    <Form.Item
                        name="paymentAccount"
                        label="付款账号"
                        rules={[{ required: true, message: '请输入付款账号' }]}
                    >
                        <Input placeholder='请输入付款账号' />
                    </Form.Item>
                    <Form.Item
                        name="bankTransferNo"
                        label="唯一标识"
                        rules={[{ required: true, message: '请输入银行转账唯一标识（如日志编号）' }]}
                    >
                        <Input placeholder='请输入银行转账唯一标识（如日志编号）' />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="返现操作"
                open={cashbackVisible}
                onOk={handleCashback}
                onCancel={() => setCashbackVisible(false)}
            >
                <Form form={form}>
                    <Form.Item
                        name="money"
                        label="返现金额"
                        rules={[
                            { required: true, message: '请输入返现金额' },
                            { validator: validatePositiveMoney }
                        ]}
                    >
                        <InputNumber style={{ width: '100%' }} precision={2} />
                    </Form.Item>
                    <Form.Item
                        name="reason"
                        label="返现原因"
                        rules={[{ required: true, message: '请输入返现原因' }]}
                    >
                        <Input.TextArea />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
});

export default EnterpriseAccountManagement; 